/**
 * Les accès de suivi ouverts aux clients — côté patron.
 *
 * Le troisième plan d'identité du plan validé : **un contact client n'est pas
 * un `auth.users`**. Il n'a ni compte ni mot de passe ; il détient un lien,
 * portant un jeton, qui ouvre une fenêtre sur **un** chantier et rien d'autre.
 *
 * Ce module ne porte que les gestes du patron — ouvrir, lister, révoquer. La
 * lecture du portail, elle, ne passe par aucun service : le visiteur n'a pas
 * de session, donc pas de contexte d'exécution, et il appelle directement les
 * fonctions `portal_*` de la base. C'est la seule exception de ce dépôt, et
 * elle est documentée là où elle vit — dans la page de suivi.
 *
 * ══════════════════════════════════════════════════════════════
 * Le jeton ne se relit pas
 * ══════════════════════════════════════════════════════════════
 * `ouvrir_acces_client` le rend **une seule fois**, en clair. La base n'en
 * garde que le condensat, comme pour un code d'invitation et pour la même
 * raison : un lien perdu se régénère, il ne se retrouve pas. L'écran doit donc
 * le montrer immédiatement, et prévenir qu'il ne réapparaîtra pas.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";

// ─── Entrées ───────────────────────────────────────────────────────────────

export const openClientAccessInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid("Identifiant de chantier attendu"),
  /** À qui on l'ouvre. Facultatif : un syndic n'a pas toujours de nom. */
  fullName: z.string().trim().max(120).optional(),
  email: z.string().trim().email("Adresse e-mail invalide").optional().or(z.literal("")),
});

export const listClientAccessInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid().optional(),
});

export const revokeClientAccessInput = z.object({
  confirmationId: z.string().optional(),
  accessId: z.string().uuid(),
});

export type OpenClientAccessInput = z.infer<typeof openClientAccessInput>;
export type ListClientAccessInput = z.infer<typeof listClientAccessInput>;
export type RevokeClientAccessInput = z.infer<typeof revokeClientAccessInput>;

// ─── Ce qu'on montre au patron ─────────────────────────────────────────────

/**
 * Un accès, tel que le patron le voit. **Sans le jeton** : il n'existe qu'une
 * fois, dans la réponse de `ouvrir_acces_client`.
 */
export interface ClientAccess {
  id: string;
  projectId: string;
  fullName: string | null;
  email: string | null;
  expiresAt: string;
  revokedAt: string | null;
  /** Nul tant que personne n'a ouvert le lien. */
  lastSeenAt: string | null;
  viewCount: number;
  createdAt: string;
}

/** Un accès vivant : ni révoqué, ni périmé. */
export function isLiveAccess(access: ClientAccess, now: Date = new Date()): boolean {
  return access.revokedAt === null && Date.parse(access.expiresAt) > now.getTime();
}

/**
 * Le client a-t-il déjà regardé ?
 *
 * Sert à une phrase, pas à une statistique : un patron qui a envoyé le lien la
 * semaine dernière veut savoir si ça a servi à quelque chose. Un lien jamais
 * ouvert est peut-être un lien jamais reçu.
 */
export function hasBeenOpened(access: ClientAccess): boolean {
  return access.viewCount > 0;
}

// ─── Port ──────────────────────────────────────────────────────────────────

export interface PortalRepository {
  /**
   * Ouvre un accès et rend le jeton **en clair, une seule fois**.
   *
   * Passe par `open_client_access()` : la génération du jeton et le calcul de
   * son échéance vivent en base, pour que ni l'écran ni l'agent ne puissent
   * s'accorder un lien plus long ou plus faible.
   */
  open(
    ctx: ExecutionContext,
    cmd: { projectId: string; fullName: string | null; email: string | null },
  ): Promise<string>;

  list(ctx: ExecutionContext, filter: { projectId?: string }): Promise<ClientAccess[]>;

  revoke(ctx: ExecutionContext, accessId: string): Promise<void>;
}

// ─── Services ──────────────────────────────────────────────────────────────

/**
 * Les rôles autorisés à ouvrir une fenêtre sur l'entreprise.
 *
 * Le terrain en est exclu, et la base l'impose aussi par `contacts_office` :
 * donner accès à un tiers est une décision commerciale, pas un geste de
 * chantier.
 */
const BUREAU = ["patron", "conducteur", "assistant"] as const;

export function createPortalServices(repo: PortalRepository) {
  /**
   * Ouvrir l'accès de suivi d'un chantier.
   *
   * `ReversibleWrite` et non `External` : rien ne part à l'extérieur. C'est le
   * patron qui transmet le lien ensuite, par le moyen qu'il veut — et c'est
   * pour ça qu'aucune confirmation N3 ne s'impose ici. Le geste qui engage
   * l'entreprise sera l'envoi, quand il existera.
   *
   * Ouvrir un second accès révoque le premier, en base. C'est voulu : deux
   * liens vivants sur un chantier, dont un que personne ne suit, est
   * exactement la situation qu'on ne veut pas.
   */
  const ouvrirAccesClient = defineService<OpenClientAccessInput, { token: string }>({
    name: "ouvrir_acces_client",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: async (ctx, input) => ({
      token: await repo.open(ctx, {
        projectId: input.projectId,
        fullName: input.fullName?.trim() || null,
        email: input.email?.trim() || null,
      }),
    }),
  });

  const listerAccesClient = defineService<ListClientAccessInput, ClientAccess[]>({
    name: "lister_acces_client",
    level: ActionLevel.Read,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) =>
      repo.list(ctx, input.projectId ? { projectId: input.projectId } : {}),
  });

  /**
   * Fermer la fenêtre.
   *
   * Immédiat : le jeton cesse d'ouvrir à l'appel suivant. L'accès n'est pas
   * supprimé mais marqué — savoir qu'un lien a existé, à qui, et combien de
   * fois il a été ouvert, vaut mieux qu'une ligne effacée.
   */
  const revoquerAccesClient = defineService<RevokeClientAccessInput, void>({
    name: "revoquer_acces_client",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => repo.revoke(ctx, input.accessId),
  });

  return { ouvrirAccesClient, listerAccesClient, revoquerAccesClient };
}

export type PortalServices = ReturnType<typeof createPortalServices>;
